import {
    Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, OnDestroy, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { GfMarkdownEditorComponent } from '../../../widgets/gf-markdown-editor/gf-markdown-editor.component';
import { ChatRenderService } from '../services/gfchat.service';
import { ChatMacroService } from '../services/gfchat.macro.service';
import { PollService } from '../../../core/services/core.poll.service';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../../../core/services/core.notification.service';
import { UiService } from '../../../core/services/core.ui.service';
import { UserService } from '../../../core/services/core.user.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { SiteConfigService } from '../../../core/services/core.site-config.service';
import { doGetPromise } from '../../../core/config/core.httpclient-utils';
import { ProjectService } from '../../../core/services/project.service';
import { ChatStatus, ChatStatusService } from '../../../core/services/core.chat-status.service';
import {
    ALL_MENTION_USER, generateRoleSlug, GFORGE_MENTION_USER, MentionConfig, MentionRole, MentionsService, MentionUser
} from '../../../core/services/core.mentio.service';

import * as _ from 'lodash';
import * as $ from 'jquery';

@Component({
    selector: 'gf-chat',
    template: require('./gfchat.component.html')
})

export class GfChatComponent implements OnChanges, OnInit, OnDestroy {
    @Input() refId;
    @Input() section;
    @Input() isStandaloneChat;
    @Input() active;
    @Input() isAdmin: boolean = false;
    /** The chat thread id, once /api/forum/x/chat has resolved it for this room */
    @Output() threadLoaded = new EventEmitter<number>();

    /**
     * Project reference used to resolve `git:<hash>` chat refs to a commit
     * link -- only meaningful in project-scoped chat, same as the existing
     * `this.projectService.members(String(this.refId), ...)` call below,
     * which already treats `refId` as the project id/unix-name here.
     */
    get projectRef(): string {
        return this.section === 'project' ? String(this.refId) : '';
    }

    @ViewChild('chatEditor', { static: false }) chatEditor!: GfMarkdownEditorComponent;

    constructor(
        private chatRenderService: ChatRenderService,
        private chatMacroService: ChatMacroService,
        private pollService: PollService,
        private http: HttpClient,
        private notificationService: NotificationService,
        private uiService: UiService,
        private userService: UserService,
        private uploadService: UploadService,
        private siteConfigService: SiteConfigService,
        private mentionsService: MentionsService,
        private projectService: ProjectService,
        private window: Window,
        private hostRef: ElementRef,
        public chatStatusService: ChatStatusService
    ) { }

    public oldMessagesLoad = 100;
    public inPollingCall = false;
    public isShown = true;
    public messages = []; //This contains the full array of messages received from the backend
    //It is a subset of the full array
    public currentTop = 0; //Points to the current top position on the full messages array
    public submit = { newMessage: '' };
    public currentForum = null;
    public currentThread = null;
    public draggingOver = false;
    public soundActive = true;
    public headerEnabled = false;
    public activityActive = false;
    public lastActivityId = 0;
    public lastOnlineUsersId = 0;
    public autoscrollActive = true;
    public pollsReady = false;
    public msgWindowSize = 15; //This indicates how many messages at once we render on the chat panel
    public oldMessagesId = null;
    public loadOldMessagesLoadingFlag = false;
    public sounds = [];
    public currentUser;
    public appEnvironment;
    public id;
    public getMentionTextRaw;
    public reqAnimTime = new Date();
    protected textSize = '';
    public composeResizing = false;
    /** Dragged compose height in px */
    private composeHeight = 0;
    /** Resting height of the compose box */
    private readonly composeMinHeight = 78;
    /** Share of the chat column the whole compose panel may take */
    private readonly composeMaxPanelShare = .6;
    private removeComposeResizeListeners: (() => void) | null = null;
    private focusHandler: (() => void) | null = null;
    private destroyed = false;
    /**
     * Where the transcript sat when this room was last taken off screen.
     *
     * The standalone chat drops a room's panel from the DOM while another
     * room is showing (*ngIf="active"), so switching back builds a fresh
     * panel sitting at the top of the history. Without remembering, coming
     * back would scroll the whole history past every time.
     */
    private savedScrollTop = 0;
    private savedAtBottom = true;
    private projectRoles: MentionRole[] = [];
    public mentions: MentionConfig | null = null;
    /** Live chat status of the users in this room, by user id; null until the first list arrives */
    public userStatuses: { [userId: string]: string } | null = null;

    ngOnInit() {
        this.currentUser = this.userService.session.getUser();
        this.appEnvironment = _.get(this.siteConfigService.getSiteConfig(), 'appEnvironment', 'production');
        
        this.id = this.section + '_' + this.refId;
        this.getMentionTextRaw = this.chatRenderService.getMentionTextRaw(_.get(this.userService.getUser(), 'unixName', ''));
        this.initializeMentionConfig();
        this.loadUserRoleSlugs().then(() => this.startChatPoll());
        this.loadSoundState();
        this.loadSounds();
        this.loadTicklerHeaderState();
        this.restoreComposeHeight();

        this.pollService.subscribe('chat-' + this.id, this.handlePollData);
        this.notificationService.subscribe('chat-' + this.id, 'GFCHAT_CURRENT_CHATROOM', this.handleCurrentChatroom);

        var draggingOver = false;
        this.notificationService.subscribe('chat-' + this.id, 'DRAGOVER_START_EVENT', () => {
            draggingOver = true;
            if (this.draggingOver !== draggingOver) {
                this.draggingOver = draggingOver;
            }
        });

        this.notificationService.subscribe('chat-' + this.id, 'DRAGOVER_END_EVENT', () => {
            this.draggingOver = false;
            draggingOver = false;
        });

        this.focusHandler = () => {
            this.pollService.run();
        };
        $(this.window).on('focus', this.focusHandler);
    }

    /**
     * The room's panel is about to be dropped or rebuilt: note where the
     * reader was, or put them back there.
     */
    ngOnChanges(changes: SimpleChanges) {
        const active = changes.active;
        if (!active || active.firstChange) {
            return;
        }

        if (active.previousValue && !active.currentValue) {
            // The view still holds the outgoing panel at this point.
            this.rememberScrollPosition();
        } else if (!active.previousValue && active.currentValue) {
            this.restoreScrollPosition();
        }
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.notificationService.unsubscribe('chat-' + this.id);
        this.pollService.unsubscribe('chat-' + this.id);
        this.stopChatPoll();
        this.mentions?.destroy();
        this.stopComposeResize();
        if (this.focusHandler) {
            $(this.window).off('focus', this.focusHandler);
            this.focusHandler = null;
        }

        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    /**
     * Checks in the polling data for the "chat_users" object for the given thread
     * and decides if there's an update for the online users list
     * 
     * @param {array} data 
     * @param {int} threadId 
     */
    getOnlineUsersUpdate = (data, threadId) => {
        // Every open room polls its own chat_users, so pick this thread's.
        var chatUsers = _.find(data, (item: any) =>
            item?.type == 'chat_users' &&
            item?.section == 'forumthread' &&
            _.get(item, 'refId', '') == threadId
        );
        if (!chatUsers) return;
        if (_.get(chatUsers, 'lastValue', 0) == this.lastOnlineUsersId) return;

        const onlineUsers = _.sortBy(_.get(chatUsers, 'items', []), ['unixName']);
        if (!onlineUsers?.length) return;

        this.currentThread.usersOnline = onlineUsers;
        this.userStatuses = Object.fromEntries(onlineUsers.map((u: any) => [u.id, u.chatStatus]));

        // The list always includes the current user, which is how a status
        // the server set (online on entering a room) reaches the picker.
        const me: any = onlineUsers.find((u: any) => this.isMe(u));
        if (me) {
            this.chatStatusService.sync(me.chatStatus);
        }

        // For some reason, activeUsers does not always equal usersOnline,
        // so let's combine/unique for @-mention purposes.
        this.currentThread.activeUsers = _.sortBy(_.uniqBy(
            _.concat(
                this.currentThread.activeUsers,
                this.currentThread.usersOnline
            ), 'id'
        ), ['unixName']);

        this.lastOnlineUsersId = chatUsers.lastValue;
    }

    /**
     * Chat's mention candidates: whoever is in the thread, plus @all and @gforge.
     *
     * Read fresh on every search rather than snapshotted -- the roster changes
     * with each poll.
     */
    getChatMentionUsers(): MentionUser[] {
        const activeUsers = (this.currentThread?.activeUsers || []).map(u => ({ ...u } as MentionUser));
        return _.uniqBy([ALL_MENTION_USER, GFORGE_MENTION_USER, ...activeUsers], 'unixName');
    }

    /**
     * Build the mention source for this chat room.
     *
     * `seed` and `roles` are read at search time rather than captured, so the
     * roster changing on every poll needs no rebuilding here.
     */
    initializeMentionConfig(): void {
        this.mentions = this.mentionsService.createMentionConfig({
            // Chat's project ref is the project id; the members endpoint keys on
            // either identifier, same as the raw call this replaced.
            project: () => this.section === 'project' && this.refId ? String(this.refId) : '',
            seed: () => this.getChatMentionUsers(),
            roles: () => this.projectRoles
        });
    }

    setMyStatus(status: ChatStatus) {
        this.chatStatusService.set(status);
    }

    /** A user's status in the online lists -- the current user's own straight away */
    statusOf(user): string | null {
        return this.isMe(user) ? this.chatStatusService.status : user?.chatStatus ?? null;
    }

    private isMe(user): boolean {
        return !!user && user.id == this.currentUser?.id;
    }

    setTextSize(sizeClass = '') {
        $('#gf-chat').removeClass(['large-text', 'extra-large-text']);
        if (sizeClass != '') {
            $('#gf-chat').addClass(sizeClass);
        }
        this.textSize = sizeClass;
    }

    handlePollData = (args) => {
        var threadId = _.get(this, 'currentThread.id', 0);
        var projectId = (this.section == 'project' ? this.refId : 0);

        var data = _.get(args, 'response.items', []);

        // Remove messages from existing list
        // that were "deleted" by incoming items
        var deleted = this.chatRenderService.getDeletedMessageIds(data, projectId);
        this.messages = this.chatRenderService.deleteMessages(this.messages, deleted);

        var items = this.chatRenderService.transformPollData(data, threadId, projectId);

        // Deduplicate: wall posts and activity log entries for the same item
        var deduped = this.chatRenderService.deduplicateActivityAndWall(this.messages, items);
        this.messages = deduped.existingMessages;
        items = deduped.newItems;

        //Checks and updates online users list
        this.getOnlineUsersUpdate(data, threadId);

        //If it's in a project (and in standalone chat), then publish the ticklers
        if (this.isStandaloneChat && projectId > 0) {
            const tickler = _.find(data, { type: 'tickler', section: 'project', refId: projectId.toString() });
            if (tickler) {
                const params = {
                    ticklers: {
                        queryTicklers: _.get(tickler, 'items', []),
                        ticklerAddQueries: []
                    },
                    section: 'project',
                    refId: projectId
                };

                this.notificationService.notifyAll('TICKLER-DATA-UPDATED', params);
            }
        }

        this.handleNewMessages(items);
    }

    //With this listener we let know the directive that it's the current visible chat
    // Note this fires in every open room, not just the one being switched to,
    // so it must not touch anything that belongs to this room -- isAdmin
    // included, which arrives per room on the @Input.
    handleCurrentChatroom = (arg) => {
        this.pollService.run();
        if (arg.section == this.section && arg.refId == this.refId && this.messages.length) {
            // Put the reader back where they were, rather than at the end:
            // being switched to is not a reason to move them.
            this.restoreScrollPosition();
        }
    }

    // //onfocus doesn't work on mobile when switching between apps or unlocking the phone
    // //this is a trick using requestAnimationFrame which only works when the browser's page is on screen
    loopFn = () => {
        const now = new Date();
        if (now.getTime() - this.reqAnimTime.getTime() > 10000) {
            // browser was suspended and did come back to focus
            this.pollService.run();
        }
        this.reqAnimTime = now;
        // this.window.requestAnimationFrame(this.loopFn);
    };

    handleNewMessages = (items) => {
        if (items.length) {
            var initialLoad = (_.size(this.messages) == 0);

            var allMessages = this.chatRenderService.appendAndPrune(
                this.messages,
                this.chatRenderService.preRenderMessages(items, this.projectRef)
            );

            this.messages = allMessages;

            if (initialLoad) {
                // First paint: put the panel at the end, don't travel there.
                this.scrollDownMsgPanel(1500, false);
            } else if (this.shouldScroll()) {
                this.scrollDownMsgPanel();
            }

            // Use items instead of pre-rendered for the raw text,
            // instead of pre-rendered HTML that may be injected.
            var recentItems = _.filter(items, this.chatRenderService.isRecent);

            // mentions/growl/ding
            var recentMentions = this.chatRenderService.getMentionMessages(recentItems);
            this.chatRenderService.showNotifications(
                _.get(this, 'currentThread.forum.forumName', 'Discussion'),
                recentMentions
            );

            if (_.size(recentMentions) > 0) {
                this.chatRenderService.playNotifySound();
            }

            // sounds
            var soundsToPlay = this.chatRenderService.getMessageSounds(recentItems);
            _.forEach(
                soundsToPlay,
                (sound) => {
                    this.chatRenderService.playSoundByName(sound, this.sounds);
                }
            );
        }
    }

    doSubmitMessage = (body) => {
        this.pollService.run();
        if (this.currentThread) {
            // No need to wait for result,
            // new message will come from poll.
            return this.http.post('/api/forummessage', {
                forumThread: this.currentThread.id,
                subject: "Chat Message",
                body: body
            }).toPromise();
        }
    }

    submitMessage = (event) => {
        // Don't show the [Enter] key if it's submitting the chat
        event.preventDefault();

        // Take the text from the editor, not from the bound model.
        if (this.chatEditor) {
            this.submit.newMessage = this.chatEditor.getMarkdown();
        }

        if (this.chatMacroService.hasMacro(this.submit.newMessage)) {
            this.chatMacroService.callMacro(this.submit.newMessage)
                .then(
                    (result) => {
                        this.messages.push(
                            this.chatRenderService.preRenderMessage(result, this.projectRef)
                        );
                        this.scrollDownMsgPanel();
                    },
                    (result) => {
                        // TODO Create a "failure" message to add?
                        console.log("reject", result);
                    }
                );
        } else {
            this.doSubmitMessage(this.submit.newMessage)
            .then(
                (response) => { },
                (response) => { console.log('msg submit fail', response); }
            );
        }
        this.clearCompose();
    }

    /**
     * Empty the compose box after sending.
     * Resetting `submit.newMessage` alone is not enough: the editor echo-guards
     */
    private clearCompose = () => {
        this.submit.newMessage = '';
        if (this.chatEditor) {
            this.chatEditor.setMarkdown('');
            this.chatEditor.suppressToolbar();
        }
    }

    /**
     * Drag the grip on the compose panel's top edge to set the box's height.
     *
     * Pointer rather than mouse events: unlike the sidebar splitters elsewhere
     * this grip is reachable on a phone, where the compose box is small enough
     * that resizing it is the whole point.
     */
    startComposeResize = (event: PointerEvent) => {
        const pane = this.getComposePane();
        if (!pane) return;

        // A second pointer going down mid-drag would otherwise strand the first
        // drag's listeners on the document.
        this.stopComposeResize();

        // Also suppresses the text selection a drag across the transcript
        // would otherwise start.
        event.preventDefault();
        this.composeResizing = true;

        const startY = event.clientY;
        const startHeight = pane.getBoundingClientRect().height;
        const maxHeight = this.getComposeMaxHeight(startHeight);

        const onMove = (moveEvent: PointerEvent) => {
            // Dragging up -- a smaller clientY -- makes the box taller.
            const desired = startHeight + (startY - moveEvent.clientY);
            this.setComposeHeight(Math.max(this.composeMinHeight, Math.min(desired, maxHeight)));
        };

        const onUp = () => {
            this.stopComposeResize();
            this.persistComposeHeight();
        };

        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);

        this.removeComposeResizeListeners = () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
    }

    /** Double-click the grip to hand the box back to its auto-grow default. */
    resetComposeHeight = () => {
        this.composeHeight = 0;
        this.hostRef.nativeElement.style.removeProperty('--gf-chat-compose-height');
        try {
            this.window.localStorage.removeItem(this.composeHeightStorageKey);
        } catch (e) { /* storage disabled -- nothing was stored to clear */ }
    }

    private stopComposeResize(): void {
        this.composeResizing = false;
        if (this.removeComposeResizeListeners) {
            this.removeComposeResizeListeners();
            this.removeComposeResizeListeners = null;
        }
    }

    /**
     * The editing surface whose height the grip drives.
     *
     * ToastUI keeps both the markdown and the WYSIWYG pane in the DOM and hides
     * the inactive one, so the visible one is picked by measurement rather than
     * by asking the editor which mode it is in.
     */
    private getComposePane(): HTMLElement | null {
        const panes: HTMLElement[] = Array.from(this.hostRef.nativeElement.querySelectorAll(
            '.chat-compose-input .toastui-editor-defaultUI .ProseMirror'));
        return panes.find(pane => pane.getBoundingClientRect().height > 0) || panes[0] || null;
    }

    /**
     * How tall the compose box may get: enough that the whole bottom panel stays
     * inside its share of the chat column, so a drag can never swallow the
     * transcript. The panel's chrome -- button row, borders, padding -- is
     * measured rather than assumed, since it differs between the embedded and
     * the standalone layout.
     */
    private getComposeMaxHeight(paneHeight: number): number {
        const host = this.hostRef.nativeElement;
        const stack = host.querySelector('.chat-center-stack');
        const panel = host.querySelector('.chat-panel-bottom');
        const stackHeight = (stack && stack.clientHeight) || this.window.innerHeight;
        const chrome = panel ? panel.getBoundingClientRect().height - paneHeight : 0;

        return Math.max(this.composeMinHeight, Math.round(stackHeight * this.composeMaxPanelShare) - chrome);
    }

    /**
     * Height is handed to CSS as a custom property rather than an inline height:
     * the box that has to grow is ToastUI's, several levels down inside the
     * editor widget, and chat-common.css is where that reach already lives.
     */
    private setComposeHeight(height: number): void {
        this.composeHeight = Math.round(height);
        this.hostRef.nativeElement.style.setProperty('--gf-chat-compose-height', this.composeHeight + 'px');
    }

    /** Embedded and standalone chat are sized independently -- one is a sidebar
     *  panel, the other a whole window. */
    private get composeHeightStorageKey(): string {
        return this.isStandaloneChat ? 'chat.compose.height.standalone' : 'chat.compose.height';
    }

    private restoreComposeHeight(): void {
        let stored: string | null = null;
        try {
            stored = this.window.localStorage.getItem(this.composeHeightStorageKey);
        } catch (e) { return; }

        const parsed = parseInt(stored || '', 10);
        if (isNaN(parsed)) return;

        // Clamped against the viewport only: nothing has been laid out yet, so
        // the panel-relative cap has to wait for the next drag.
        const max = Math.max(this.composeMinHeight,
            Math.round(this.window.innerHeight * this.composeMaxPanelShare));
        this.setComposeHeight(Math.max(this.composeMinHeight, Math.min(parsed, max)));
    }

    private persistComposeHeight(): void {
        if (!this.composeHeight) return;
        try {
            this.window.localStorage.setItem(this.composeHeightStorageKey, String(this.composeHeight));
        } catch (e) { /* storage disabled -- the size just lasts the session */ }
    }

    toggleSound = () => {
        this.soundActive = !this.soundActive;
        this.userService.preference.setPreference('chat_sound_active', (!this.soundActive).toString());
    }

    toggleActivity = () => {
        this.setActivityToggle(!this.activityActive);
    }

    toggleHeader = () => {
        this.headerEnabled = !this.headerEnabled;
        this.notificationService.notifyAll('GFCHAT-HEADER-TOGGLE', { headerEnabled: this.headerEnabled });
        this.userService.preference.setPreference('chat_tickler_header', this.headerEnabled.toString());
    }

    loadOldMessages = () => {
        if (this.loadOldMessagesLoadingFlag) {
            return;
        }
        this.loadOldMessagesLoadingFlag = true;

        const before = _.min(_.map(this.messages, 'id'));

        doGetPromise(this.http, 
            '/api/forummessage/x/forummessagesbetweenidsbythreadid',
            {
                threadId: this.currentThread.id,
                before: before
            }
        ).then((response) => {
                // NOTE: fetching old messages does not include:
                // deleteMessages, updateUsersList, mentions/growl/ding, sounds

                var items = _.sortBy(
                    this.chatRenderService.chatMessages(
                        { items: _.get(response, 'items', []) }
                    ),
                    'id'
                );

                if (items.length > 0) {
                    // Reverse order to prepend old messages
                    this.messages = this.chatRenderService.appendAndPrune(
                        this.chatRenderService.preRenderMessages(items, this.projectRef),
                        this.messages
                    );
                }                
            }, () => {}
        ).finally( () => {
            this.loadOldMessagesLoadingFlag = false;
        });
    }

    filesUpload = (event: any) => {
        const input = event.target as HTMLInputElement;
        const files = input.files;
        this.fileSelected(files);
    }

    fileSelected = ($files: any) => {
        //Loop through added files and upload
        if ($files !== undefined) {
            for (var i = 0; i < $files.length; i++) {
                var file = $files[i],
                    url = '/api/forummessage/' + this.currentThread.id + '/attachment';

                this.uploadService.uploadBinary(file, url)
                    .then(() => {
                        this.uiService.message.showSuccess("Attachment " + file.name + " uploaded successfully");
                    }, (error) => {
                        var errorMessage = "Attachment " + file.name + " failed to upload.";
                        if (error.status == 413) {
                            errorMessage += " The file exceeded the maximum allowed size.";
                        }
                        this.uiService.message.showError(errorMessage);
                    });
            }
        }
    }

    /**
     * Target for images uploaded from the compose box
     *
     * Uploads go through UploadService, which posts section + refId and has the
     * backend authorise them (FilesystemService -> USER_CAN_WRITE_RELATED_OBJECT
     * -> ForumPlugin). That handler accepts two sections: 'forum' keyed by forum
     * id, and 'forummessage' keyed by message id.
     *
     */
    get chatUploadRefId(): number {
        return _.get(this, 'currentThread.forum.id', 0);
    }

    submitSound = (soundName) => {
        this.doSubmitMessage('[' + soundName + ']');
    }

    setActivityToggle = (newVal) => {
        if (newVal !== this.activityActive) {
            this.activityActive = newVal;
            this.scrollDownMsgPanel();
            //This is the last poll added at initial state, so we can set the directive as polls ready here
            this.setPollsReady(); //This will tell the parent controller when the directive added all polls needed

            this.userService.preference.setPreference('chat_activity_active', newVal);
        }
    }

    /**
     * Pre-load the current user's project role slugs so that
     * hasMention() can detect role-based mentions from the very first poll.
     * Also fetches the full project role list for the autocomplete dropdown.
     */
    loadUserRoleSlugs = (): Promise<void> => {
        if (this.section !== 'project' || !this.refId) {
            return Promise.resolve();
        }
        const currentUnixName = _.get(this.currentUser, 'unixName', '');
        if (!currentUnixName) {
            return Promise.resolve();
        }
        const currentUserId = _.get(this.currentUser, 'id', 0);

        // Fetch both: current user's roles (for hasMention) and all project roles (for autocomplete)
        const memberPromise = this.projectService.members(String(this.refId), {
            rel: 'roles',
            unixName: currentUnixName
        }).then((memberResponse) => {
            const members = _.get(memberResponse, 'items', []);
            const me = _.find(members, (m: any) =>
                (currentUserId && m.id === currentUserId) || m.unixName === currentUnixName
            );
            if (!me) {
                return;
            }
            const myRoles = me.roles;
            if (!myRoles || !myRoles.length) {
                return;
            }
            const myRoleSlugs = myRoles.map(
                (r: any) => r.roleSlug || generateRoleSlug(r.roleName)
            );
            this.chatRenderService.setUserRoleSlugs(myRoleSlugs);
        }, () => {});

        const rolesPromise = this.projectService.roles(String(this.refId))
            .then((response) => {
                const items = _.get(response, 'items', []);
                this.projectRoles = items
                    .filter((r: any) => r.roleName)
                    .map((r: any) => ({
                        roleSlug: r.roleSlug || generateRoleSlug(r.roleName),
                        roleName: r.roleName
                    }));
                // Roles arrive after the source was built; re-run so the very
                // first '@' offers them too.
                this.mentions?.refresh();
            }, () => {});

        return Promise.all([memberPromise, rolesPromise]).then(() => {});
    }

    startChatPoll = () => {

        if (this.inPollingCall) {
            return false;
        }
        this.inPollingCall = true;

        doGetPromise(this.http,
            '/api/forum/x/chat',
            {
                //threadId: ctrl.threadId,
                refId: this.refId,
                section: this.section
            }
        ).then((response) => {
            // The room was closed before its thread came back: adding polls
            // now would leave nothing to remove them, and the chat poll would
            // keep marking the user online in the room.
            if (this.destroyed) {
                this.inPollingCall = false;
                return;
            }

            this.currentThread = response;
            // The source was built before the roster existed, and the editor
            // shows the bound list as-is on a bare '@' -- publish the room's
            // people now so the first '@' offers them.
            this.mentions?.refresh();
            this.threadLoaded.emit(_.get(response, 'id', 0));

            var lastChatId = 0;
            var lastActivityId = 0;
            var lastWallpostId = 0;
            this.lastOnlineUsersId = 0;

            this.pollService.sleepWorkaround = true;
            if (this.pollService.running) {
                this.pollService.pause();
            }
            this.pollService.add('chat', 'forumthread', _.get(this, 'currentThread.id', 0), lastChatId);
            this.pollService.add('chat_users', 'forumthread', _.get(this, 'currentThread.id', 0), this.lastOnlineUsersId);
            if (this.section == 'project') {
                this.pollService.add('activity', 'project', _.get(this, 'refId', 0), lastActivityId);
                //Project wide ticklers are only needed for standalone chat
                if (this.isStandaloneChat) {
                    this.pollService.add('tickler', 'project', _.get(this, 'refId', 0));
                }
            }
            this.pollService.add('wall', 'user', _.get(this, 'currentUser.id', 0), lastWallpostId);
            //Force poll to run to populate the chatroom as soon as possible
            this.pollService.run();

            //Adding activity poll is enabled
            //@TODO this will be cleaner with a returning promise so we can run code needed here instead
            //inside loadActivityState()
            this.loadActivityState();
            this.inPollingCall = false;

        }, (data) => {
            console.log(data);
            this.inPollingCall = false;
            // Resume polling
            this.pollService.run();
        });

    }

    /**
     * Drop the polls started in startChatPoll(). Each room is responsible for
     * its own: the parent only knows the project, and the backend marks the
     * user online in a room on every chat poll, so a poll left behind keeps
     * a closed room "active" across reloads. The wall poll is shared with the
     * top menu, so it stays.
     */
    stopChatPoll = () => {
        const threadId = _.get(this, 'currentThread.id', 0);
        if (!threadId) {
            return;
        }
        this.pollService.remove('chat', 'forumthread', threadId);
        this.pollService.remove('chat_users', 'forumthread', threadId);
        if (this.section == 'project') {
            this.pollService.remove('activity', 'project', this.refId);
            this.pollService.remove('tickler', 'project', this.refId);
        }
    }

    /**
     * If chat window is scrolled from the bottom and a new message arrives
     * return false to avoid automatic scrolling.
     *
     * @return {Boolean}
     */
    shouldScroll = () => {
        const container = this.getMessagePanel();
        if (! container) return false;
        return this.isAtBottom(container);
    }

    /** This room's transcript, while it is the one on screen. */
    private getMessagePanel(): HTMLElement | null {
        if (! this.id) return null;
        const container = $('#chat-messages-box_' + this.id)[0];
        return container?.tagName ? container : null;
    }

    /**
     * A delta value is used so if scroll is up a bit it counts as the bottom
     * anyway. Delta height is approx. 1 message line + margins + space
     * between messages.
     */
    private isAtBottom(container: HTMLElement): boolean {
        var delta = 200; //expected limit, at most 200px from bottom
        var position = container.scrollHeight - container.scrollTop;
        return (position >= container.clientHeight - delta
            && position <= container.clientHeight + delta);
    }

    private rememberScrollPosition(): void {
        const container = this.getMessagePanel();
        if (! container) return;

        this.savedScrollTop = container.scrollTop;
        // Held separately: messages that land while the room is off screen
        // make the transcript taller, and a reader who was at the end wants
        // the new end, not the old offset.
        this.savedAtBottom = this.isAtBottom(container);
    }

    private restoreScrollPosition(): void {
        // Already on screen, so there is nothing to put back and the reader is
        // wherever they have scrolled to. Reachable because a project sits in
        // both the Active and the Projects list as two objects: clicking the
        // second one announces a room that is already current, and acting on
        // a position saved during an earlier visit would move the reader for
        // no reason.
        if (this.getMessagePanel()) {
            return;
        }

        this.whenPanelReady((container) => {
            container.scrollTop = this.savedAtBottom
                ? container.scrollHeight
                : this.savedScrollTop;
        });
    }

    private timeoutId;

    /**
     * Run `action` once this room's transcript is on screen. A room being
     * switched to renders a beat after it is told it is current, so keep
     * looking until it turns up.
     */
    private whenPanelReady(action: (container: HTMLElement) => void, wait: number = 250): void {
        if (this.timeoutId) clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(
            () => {
                const container = this.getMessagePanel();
                if (! container) {
                    const nextWait = Math.max(wait * 2, 1000);
                    this.whenPanelReady(action, nextWait);
                    return;
                }
                action(container);
            },
            wait
        );
    }

    /**
     * @param smooth Animate the travel. Worth it to draw the eye to a message
     *               that just arrived; not for placing a panel that the reader
     *               has not seen yet, where it just replays the history.
     */
    scrollDownMsgPanel(wait: number = 250, smooth: boolean = true) {
        this.whenPanelReady(
            (container) => {
                if (! smooth) {
                    container.scrollTop = container.scrollHeight;
                    return;
                }
                const elem: any = $('#chat-messages-box_' + this.id + ' .message-list-end');
                const scrollParams = { behavior: 'smooth', block: "end", inline: "nearest" };
                if (elem.length) elem[0].scrollIntoView(scrollParams);
            },
            wait
        );
    }

    loadSoundState = () => {
        this.userService.preference.getPreference('chat_sound_active', { 'default': true })
            .then(
                (response: any) => {
                    this.soundActive = (response.preferenceValue) ? true : false;
                }, () => { this.soundActive = true; }
            );
    }

    loadActivityState = () => {
        this.userService.preference.getPreference('chat_activity_active', { 'default': true })
            .then(
                (r: any) => {
                    var newVal = (r.preferenceValue) ? true : false;
                    this.setActivityToggle(newVal);
                }, () => { }
            );
    }

    loadTicklerHeaderState = () => {
        //Prevent ticklers from being displayed on any smaller screen
        if (this.window.innerWidth <= 480) {
            this.headerEnabled = false;
            return;
        }

        this.userService.preference.getPreference('chat_tickler_header', { default: true })
            .then(
                (r: any) => {
                    var newVal = (r.preferenceValue) ? true : false;
                    this.headerEnabled = newVal;
                    this.notificationService.notifyAll('GFCHAT-HEADER-TOGGLE', { headerEnabled: this.headerEnabled });
                }, () => { }
            );
    }

    setPollsReady = () => {
        if (!this.pollsReady) {
            this.pollsReady = true;
        }
    }

    loadSounds = () => {
        doGetPromise(
            this.http,
            '/api/docman/Sounds/folder',
            { 'projectId': this.refId, 'rel': 'currentfiles' }
        )
            .then(
                (response) => {
                    // ctrl.sounds = _.get(response, 'data.rel.currentfiles', []);
                    this.sounds = _.map(
                        _.get(response, 'data.rel.currentfiles', []),
                        (item) => {
                            var key = _.get(item, 'docmanFile.description', false);
                            var url = _.get(item, 'url', false);
                            if (key && url) {
                                return {
                                    'name': key,
                                    'url': url
                                };
                            }
                            return false;
                        }
                    ).filter(Boolean);
                    this.chatRenderService.setSoundNames(this.sounds.map((item) => item.name));
                },
                () => {
                    this.sounds = [];  // default value
                    this.chatRenderService.setSoundNames([]);
                }
            );
    }
}
